import { useAuth } from "@/context/AuthContext";
import { GraduationCap, LogOut } from "lucide-react";

const ROLE_LABEL = {
  super_admin: { text: "Super Admin", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  admin: { text: "Teacher", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
  student: { text: "Student", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
};

const Brand = () => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-emerald-400 flex items-center justify-center shadow-lg shadow-emerald-500/20">
      <GraduationCap className="w-5 h-5 text-slate-950" />
    </div>
    <span className="font-display font-extrabold text-lg tracking-tight">Vivek<span className="text-emerald-400"> AI</span></span>
  </div>
);

export default function AppShell({ nav = [], active, onNav, children }) {
  const { user, logout } = useAuth();
  const role = ROLE_LABEL[user?.role] || ROLE_LABEL.student;
  return (
    <div className="min-h-screen bg-[#090D16] lg:pl-64">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-[#1E293B] bg-[#0B101B] px-4 py-6 z-40" data-testid="app-sidebar">
        <div className="px-2"><Brand /></div>
        <nav className="mt-10 space-y-1 flex-1">
          {nav.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => onNav(n.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-[background-color,color] duration-200 ${active === n.id ? "bg-emerald-400/10 text-emerald-300" : "text-slate-400 hover:text-slate-100 hover:bg-[#111726]"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
              {active === n.id && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400" />}
            </button>
          ))}
        </nav>
        <div className="rounded-2xl bg-[#111726] border border-[#1E293B] p-3 flex items-center gap-3">
          {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />
            : <div className="w-9 h-9 rounded-full bg-[#1E293B] flex items-center justify-center text-sm font-semibold">{user?.name?.[0]?.toUpperCase()}</div>}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border mt-0.5 ${role.cls}`}>{role.text}</span>
          </div>
          <button data-testid="logout-button" onClick={logout} title="Logout" className="text-slate-500 hover:text-rose-400 transition-colors"><LogOut className="w-4 h-4" /></button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-40 bg-[#090D16]/85 backdrop-blur-xl border-b border-[#1E293B]">
        <div className="px-5 h-14 flex items-center justify-between">
          <Brand />
          <button data-testid="logout-button-mobile" onClick={logout} className="w-9 h-9 rounded-lg bg-[#111726] border border-[#1E293B] flex items-center justify-center text-slate-400 hover:text-rose-400"><LogOut className="w-4 h-4" /></button>
        </div>
        {nav.length > 1 && (
          <div className="flex gap-1 px-4 pb-2 overflow-x-auto">
            {nav.map((n) => (
              <button key={n.id} data-testid={`nav-mobile-${n.id}`} onClick={() => onNav(n.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${active === n.id ? "bg-emerald-400 text-slate-950" : "text-slate-400 bg-[#111726]"}`}>
                <n.icon className="w-3.5 h-3.5" /> {n.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 py-8 lg:py-10">{children}</main>
    </div>
  );
}
